import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Save, RotateCcw, Trash2, Wand2 } from "lucide-react";
import { interpretAnnotation, type InterpretedAnnotation } from "@/lib/pharmacology/annotationInterpreter";
import { toast } from "@/lib/safeToast";

export function SmartAnnotationsPanel({ substanceId }: { substanceId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [draft, setDraft] = useState<InterpretedAnnotation | null>(null);

  const { data: history = [] } = useQuery({
    queryKey: ["sca", substanceId],
    enabled: !!substanceId,
    queryFn: async () => (await supabase.from("substance_clinical_annotations").select("*").eq("substance_id", substanceId).order("created_at", { ascending: false }).limit(30)).data ?? [],
  });

  function interpret() {
    if (!text.trim()) return toast.error("Cole uma anotação clínica.");
    setDraft(interpretAnnotation(text));
  }

  async function save() {
    if (!text.trim()) return;
    const interpreted = draft ?? interpretAnnotation(text);
    const { error } = await supabase.from("substance_clinical_annotations").insert({
      owner_id: user!.id,
      substance_id: substanceId,
      raw_note: text,
      interpreted_json: interpreted as any,
      confidence_0_100: interpreted.confidence,
      review_status: "rascunho_ia_local",
    });
    if (error) return toast.error(error.message);
    toast.success("Anotação salva");
    setText(""); setDraft(null);
    qc.invalidateQueries({ queryKey: ["sca", substanceId] });
  }

  async function applyToPD() {
    if (!draft) return;
    const rows = [
      ...draft.benefits.map((b) => ({ effect_key: b.effect_key, benefit_0_100: b.value, note: b.reason })),
      ...draft.adverse.map((a) => ({ effect_key: a.effect_key, adverse_0_100: a.value, note: a.reason })),
    ];
    for (const r of rows) {
      const { data: existing } = await supabase.from("substance_effect_profiles").select("id").eq("substance_id", substanceId).eq("effect_key", r.effect_key).maybeSingle();
      const payload: any = { owner_id: user!.id, substance_id: substanceId, ...r, evidence_level: "rascunho", confidence_0_100: draft.confidence };
      if (existing?.id) {
        await supabase.from("substance_effect_profiles").update(payload).eq("id", existing.id);
      } else {
        await supabase.from("substance_effect_profiles").insert(payload);
      }
    }
    toast.success("Sugestões aplicadas em PD (revise!)");
    qc.invalidateQueries({ queryKey: ["sep", substanceId] });
  }

  async function remove(id: string) {
    if (!confirm("Excluir anotação?")) return;
    await supabase.from("substance_clinical_annotations").delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["sca", substanceId] });
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="font-medium">Anotação farmacológica / clínica</div>
          <Badge variant="outline" className="ml-auto">Heurística local</Badge>
        </div>
        <Textarea rows={5} placeholder="Ex.: Clonidina reduz descarga autonômica, ajuda sono, ansiedade física e irritabilidade, mas pode causar sedação, hipotensão ortostática e piora cognitiva se dose alta." value={text} onChange={(e) => setText(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={interpret} variant="secondary"><Wand2 className="h-4 w-4 mr-1.5" />Interpretar</Button>
          <Button size="sm" onClick={save}><Save className="h-4 w-4 mr-1.5" />Salvar anotação</Button>
          {draft ? <Button size="sm" variant="outline" onClick={applyToPD}>Aplicar sugestões em PD</Button> : null}
          {draft ? <Button size="sm" variant="ghost" onClick={() => setDraft(null)}><RotateCcw className="h-4 w-4 mr-1.5" />Limpar rascunho</Button> : null}
        </div>

        {draft && (
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg border p-3 bg-success/5">
              <div className="text-xs font-medium text-success mb-1">Benefícios sugeridos ({draft.benefits.length})</div>
              {draft.benefits.length === 0 ? <div className="text-xs text-muted-foreground">Nenhum benefício detectado.</div> :
                <ul className="space-y-1 text-xs">
                  {draft.benefits.map((b) => <li key={b.effect_key}><span className="font-medium">{b.label}</span> · {b.value}/100 <span className="text-muted-foreground">— {b.reason}</span></li>)}
                </ul>}
            </div>
            <div className="rounded-lg border p-3 bg-destructive/5">
              <div className="text-xs font-medium text-destructive mb-1">Riscos sugeridos ({draft.adverse.length})</div>
              {draft.adverse.length === 0 ? <div className="text-xs text-muted-foreground">Nenhum risco detectado.</div> :
                <ul className="space-y-1 text-xs">
                  {draft.adverse.map((a) => <li key={a.effect_key}><span className="font-medium">{a.label}</span> · {a.value}/100 <span className="text-muted-foreground">— {a.reason}</span></li>)}
                </ul>}
            </div>
            <div className="md:col-span-2 rounded-lg border p-3 text-xs">
              <div className="font-medium mb-1">Mecanismo provável</div>
              {draft.mechanism.length === 0 ? <span className="text-muted-foreground">Não inferido.</span> : <span>{draft.mechanism.join(" · ")}</span>}
              <div className="mt-1 text-muted-foreground">Confiança rascunho: {draft.confidence}/100 — revisão médica obrigatória.</div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-2">
        <div className="font-medium text-sm">Histórico de anotações</div>
        {history.length === 0 ? <div className="text-xs text-muted-foreground">Sem anotações salvas para esta substância.</div> : (
          <div className="space-y-2">
            {history.map((h: any) => (
              <div key={h.id} className="rounded-md border p-3 text-xs space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline">{h.review_status}</Badge>
                  <div className="text-muted-foreground">{new Date(h.created_at).toLocaleString()}</div>
                  <Button size="icon" variant="ghost" onClick={() => remove(h.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="whitespace-pre-wrap">{h.raw_note}</div>
                {h.interpreted_json ? (
                  <details><summary className="cursor-pointer text-muted-foreground">Ver interpretação</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-[10px] bg-muted/50 p-2 rounded">{JSON.stringify(h.interpreted_json, null, 2)}</pre>
                  </details>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
